import { inject, Injector, runInInjectionContext } from '@angular/core';
import { CanActivateFn, UrlTree } from '@angular/router';
import { firstValueFrom, Observable } from 'rxjs';

type GuardResult = boolean | UrlTree;

/** Executes guards sequentially. Stops and propagates the result on first
 *  non-true outcome — either `false` (block) or a `UrlTree` (redirect). */
export function asyncOrderGuard(guards: CanActivateFn[]): CanActivateFn {
  return async (route, state): Promise<GuardResult> => {
    const injectionContext = inject(Injector);

    for (const guard of guards) {
      const result = await runInInjectionContext(injectionContext, () => {
        const guardResult = guard(route, state);

        if (guardResult instanceof Observable) {
          return firstValueFrom(guardResult);
        }
        if (guardResult instanceof Promise) {
          return guardResult;
        }
        return Promise.resolve(guardResult);
      }).catch((err) => {
        console.error('Guard error:', err);
        return false;
      });

      // Anything that isn't `true` short-circuits the chain. This includes
      // both `false` (deny) and a `UrlTree` (redirect) — Router handles each.
      if (result !== true) {
        return result as GuardResult;
      }
    }

    return true;
  };
}
